import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../api";
import { useAuth } from "../utils/authprovider";
import { track } from "../utils/analytics";

import "../styles/SignInPage.css";
import "../styles/buttons.css";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    track("page_view", {
      page_location: window.location.href,
      page_title: "Sign In",
    });
  }, []);

  useEffect(() => {
    document.body.classList.add("sign-in-body");
    return () => document.body.classList.remove("sign-in-body");
  }, []);

  const handlePostSignIn = useCallback((data) => {
    window.gtag?.("set", { user_id: data.user_id });

    const {
      user_id: userId,
      onboarding_progress: progress,
      account_type: acctType,
      selected_venues: venues = [],
      subscription_active: active,
    } = data;

    if (progress === "registered") {
      return navigate(`/user_verification/${userId}`);
    }

    if (acctType === "business") {
      if (!active) {
        return navigate(`/business_plans/${userId}`);
      }
      return navigate(`/dashboard/${userId}`);
    }

    if (progress === "email_verified") {
      if (!active) {
        return navigate(`/couples_plans/${userId}`, { state: { expired: true } });
      }
      if (active && venues.length === 0) {
        return navigate(`/find_venues/${userId}`);
      }
      return navigate(`/dashboard/${userId}`);
    }
    navigate(`/dashboard/${userId}`);
  }, [navigate]);

  const signInWithEmail = async () => {
    track("login_submit", { method: "email" });
    setEmailError("");
    setPasswordError("");

    try {
      const { data } = await api.post(
        "/sign_in_user/",
        { email, pwd: password },
        { withCredentials: true }
      );

      if (data.status === "success") {
        localStorage.setItem("accountType", data.account_type || "");
        if (data.cookie) {
          localStorage.setItem("bd2mSessionToken", data.cookie);
        }
        login();
        track("login_success", { method: "email" });
        window.gtag?.("set", "user_properties", { login_method: "email" });
        handlePostSignIn(data);
        return;
      }

      setEmailError(data.message || "Unexpected error signing in.");
      track("login_error", {
        method: "email",
        message: data.message || "api_fail",
      });
    } catch (err) {
      console.error("Email sign-in error:", err);
      setEmailError("The email and password you entered did not match our records.");
      track("login_error", {
        method: "email",
        message: err.message || "backend_error",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let valid = true;
    const missing = [];

    if (!email) {
      setEmailError("Email is required.");
      valid = false;
      missing.push("email");
    }
    if (!password) {
      setPasswordError("Password is required.");
      valid = false;
      missing.push("password");
    }

    if (!valid) {
      track("login_validation_error", { fields: missing.join(",") });
      return;
    }

    signInWithEmail();
  };

  return (
    <div className="sign-in-body">
      <div className="sign-in-container">
        <div className="logo">
          <a
            href="/"
            onClick={() =>
              track("navigation_click", {
                location: "sign_in",
                nav_item: "Logo",
                destination: "/",
              })
            }
          >
            BestDay2Marry
          </a>
        </div>

        <div className="sign-in-form">
          <h1>Sign In</h1>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={emailError ? "error" : ""}
              />
              {emailError && <div className="error-message">{emailError}</div>}
            </div>

            <div className="form-group">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? "error" : ""}
              />
              {passwordError && <div className="error-message">{passwordError}</div>}

              <div className="forgot-link">
                <button
                  type="button"
                  onClick={() => {
                    track("navigation_click", {
                      location: "sign_in",
                      nav_item: "Forgot Password",
                      destination: "/forgot_password",
                    });
                    navigate("/forgot_password");
                  }}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary">
              Log in
            </button>

            <div className="sign-up-container">
              <p id="sign-up-msg">
                Don’t have an account?{" "}
                <a
                  href="/signup"
                  className="sign-up-link"
                  onClick={() =>
                    track("navigation_click", {
                      location: "sign_in",
                      nav_item: "Sign Up",
                      destination: "/signup",
                    })
                  }
                >
                  Sign up
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
